import React, { useState, useEffect } from 'react';
import { Plus, Webhook, Trash2, Edit, ToggleLeft, ToggleRight, Settings, Send, Layout } from 'lucide-react';
import { apiRequest } from '../config/api';
import { buttonStyles } from '../utils/colors';
import { useToast } from '../context/ToastContext';
import ThankYouPageBuilder from './ThankYouPageBuilder';

const INTEGRATION_TYPES = {
  webhook: {
    label: 'Webhook',
    icon: Webhook,
    description: 'Send form data to a webhook URL',
    color: 'text-brand-600'
  },
  thank_you_page_customizer: {
    label: 'Thank You Page Customizer',
    icon: Layout,
    description: 'Create a custom thank you page with sections',
    color: 'text-purple-600'
  }
  // email_creator: {
  //   label: 'Email to Creator',
  //   icon: Mail,
  //   description: 'Send email to form creator',
  //   color: 'text-green-600'
  // },
  // email_responder: {
  //   label: 'Email to Responder',
  //   icon: Mail,
  //   description: 'Send email to form submitter',
  //   color: 'text-purple-600'
  // }
};

function IntegrationManager({ formId, formVersionId = null }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [pageBuilderIntegration, setPageBuilderIntegration] = useState(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    fetchIntegrations();
  }, [formId, formVersionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const endpoint = formVersionId
        ? `/integrations/forms/${formId}/versions/${formVersionId}`
        : `/integrations/forms/${formId}`;

      const response = await apiRequest(endpoint);
      const data = await response.json();
      setIntegrations(data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      showError('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIntegration = async (integrationData) => {
    try {
      const endpoint = formVersionId
        ? `/integrations/forms/${formId}/versions/${formVersionId}`
        : `/integrations/forms/${formId}`;

      const response = await apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(integrationData),
      });

      if (response.ok) {
        showSuccess('Integration created successfully');
        fetchIntegrations();
        setShowCreateModal(false);
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to create integration');
      }
    } catch (error) {
      console.error('Error creating integration:', error);
      showError('Failed to create integration');
    }
  };

  const handleUpdateIntegration = async (integrationId, updateData) => {
    try {
      const endpoint = formVersionId
        ? `/integrations/versions/${integrationId}`
        : `/integrations/forms/${integrationId}`;

      const response = await apiRequest(endpoint, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        showSuccess('Integration updated successfully');
        fetchIntegrations();
        setEditingIntegration(null);
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to update integration');
      }
    } catch (error) {
      console.error('Error updating integration:', error);
      showError('Failed to update integration');
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) {
      return;
    }

    try {
      const endpoint = formVersionId
        ? `/integrations/versions/${integrationId}`
        : `/integrations/forms/${integrationId}`;

      const response = await apiRequest(endpoint, {
        method: 'DELETE',
      });

      if (response.ok) {
        showSuccess('Integration deleted successfully');
        fetchIntegrations();
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to delete integration');
      }
    } catch (error) {
      console.error('Error deleting integration:', error);
      showError('Failed to delete integration');
    }
  };

  const toggleIntegration = async (integration) => {
    const updateData = { is_active: !integration.is_active };
    await handleUpdateIntegration(integration.id, updateData);
  };

  const handleTestWebhook = async (integrationId) => {
    try {
      const response = await apiRequest(`/integrations/test/${integrationId}`, {
        method: 'POST',
      });

      if (response.ok) {
        showSuccess('Test webhook sent successfully!');
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to send test webhook');
      }
    } catch (error) {
      console.error('Error sending test webhook:', error);
      showError('Failed to send test webhook');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Empty State */}
      {integrations.length === 0 && (
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-xl py-12 px-6">
          <div className="max-w-md mx-auto text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center">
              <Webhook className="w-7 h-7 text-brand-600 dark:text-brand-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No integrations yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Connect your form to webhooks, custom thank-you pages, and more.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className={`${buttonStyles.primary} inline-flex items-center space-x-2`}
            >
              <Plus className="w-4 h-4" />
              <span>Add Integration</span>
            </button>
          </div>

          {/* Available types as subtle hints */}
          <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 max-w-lg mx-auto">
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider text-center mb-4">Available integrations</p>
            <div className="flex justify-center gap-6">
              {Object.entries(INTEGRATION_TYPES).map(([type, info]) => {
                const Icon = info.icon;
                return (
                  <button
                    key={type}
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Icon className={`w-4 h-4 ${info.color}`} />
                    <span>{info.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Integrations List */}
      {integrations.length > 0 && (
        <>
          {/* List header */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {integrations.length} integration{integrations.length !== 1 ? 's' : ''} configured
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Integration
            </button>
          </div>

          {/* Cards */}
          <div className="space-y-4">
            {integrations.map((integration) => {
              const typeInfo = INTEGRATION_TYPES[integration.integration_type];
              const Icon = typeInfo?.icon || Settings;
              return (
                <div key={integration.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                  <div className="p-5 flex items-start gap-4">
                    {/* Icon */}
                    <div className={`flex-shrink-0 p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 ${typeInfo?.color || 'text-gray-600'}`}>
                      <Icon className="w-5 h-5" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{integration.name}</h3>
                        <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          integration.is_active
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${integration.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                          {integration.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {formVersionId && (
                          <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                            Version-specific
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{typeInfo?.description || integration.integration_type}</p>

                      {/* Config details inline */}
                      {integration.integration_type === 'webhook' && integration.config.url && (
                        <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-md px-2.5 py-1.5">
                          <span className="font-medium text-gray-600 dark:text-gray-300">URL</span>
                          <span className="truncate font-mono">{integration.config.url}</span>
                        </div>
                      )}
                      {integration.integration_type === 'thank_you_page_customizer' && integration.config.page_title && (
                        <div className="mt-2.5 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-md px-2.5 py-1.5">
                          <span><span className="font-medium text-gray-600 dark:text-gray-300">Title</span> {integration.config.page_title}</span>
                          {integration.config.page_background_color && (
                            <span className="flex items-center gap-1.5">
                              <span className="w-3 h-3 rounded border border-gray-200 dark:border-gray-600" style={{ backgroundColor: integration.config.page_background_color }}></span>
                              {integration.config.page_background_color}
                            </span>
                          )}
                        </div>
                      )}
                      {(integration.integration_type === 'email_creator' || integration.integration_type === 'email_responder') && integration.config.subject && (
                        <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-md px-2.5 py-1.5">
                          <span className="font-medium text-gray-600 dark:text-gray-300">Subject</span>
                          <span className="truncate">{integration.config.subject}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <button
                        onClick={() => toggleIntegration(integration)}
                        className={`p-2 rounded-lg transition-colors ${integration.is_active ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        title={integration.is_active ? 'Disable integration' : 'Enable integration'}
                      >
                        {integration.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => setEditingIntegration(integration)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        title="Edit integration"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {integration.integration_type === 'thank_you_page_customizer' && (
                        <button
                          onClick={() => setPageBuilderIntegration(integration)}
                          className="p-2 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                          title="Customize thank you page"
                        >
                          <Layout className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteIntegration(integration.id)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Delete integration"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingIntegration) && (
        <IntegrationModal
          integration={editingIntegration}
          onSave={editingIntegration ? handleUpdateIntegration : handleCreateIntegration}
          onTestWebhook={handleTestWebhook}
          onClose={() => {
            setShowCreateModal(false);
            setEditingIntegration(null);
          }}
        />
      )}

      {/* Thank You Page Builder */}
      {pageBuilderIntegration && (
        <ThankYouPageBuilder
          integration={pageBuilderIntegration}
          onClose={() => setPageBuilderIntegration(null)}
        />
      )}
    </div>
  );
}

function IntegrationModal({ integration, onSave, onTestWebhook, onClose }) {
  const [formData, setFormData] = useState({
    integration_type: integration?.integration_type || 'webhook',
    name: integration?.name || '',
    is_active: integration?.is_active ?? true,
    config: integration?.config || {}
  });
  const [testingWebhook, setTestingWebhook] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate based on integration type
    if (formData.integration_type === 'webhook' && !formData.config.url) {
      alert('Webhook URL is required');
      return;
    }

    if ((formData.integration_type === 'email_creator' || formData.integration_type === 'email_responder') &&
        !formData.config.subject) {
      alert('Email subject is required');
      return;
    }

    if (formData.integration_type === 'thank_you_page_customizer' && !formData.config.page_title) {
      alert('Page title is required');
      return;
    }

    const submitData = {
      integration_type: formData.integration_type,
      name: formData.name,
      is_active: formData.is_active,
      config: formData.config
    };

    if (integration) {
      onSave(integration.id, submitData);
    } else {
      onSave(submitData);
    }
  };

  const updateConfig = (key, value) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [key]: value }
    }));
  };

  const handleTestWebhook = async () => {
    if (!integration) return; // Only available for existing integrations
    
    setTestingWebhook(true);
    try {
      await onTestWebhook(integration.id);
    } finally {
      setTestingWebhook(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800/95 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
  <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {integration ? 'Edit Integration' : 'Create Integration'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <span className="sr-only">Close</span>
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Integration Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Integration Type
            </label>
            <select
              value={formData.integration_type}
              onChange={(e) => setFormData(prev => ({ ...prev, integration_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
              disabled={!!integration} // Can't change type when editing
            >
              {Object.entries(INTEGRATION_TYPES).map(([type, info]) => (
                <option key={type} value={type}>{info.label}</option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Integration Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
              placeholder="e.g., My webhook"
              required
            />
          </div>

          {/* Type-specific configuration */}
          {formData.integration_type === 'webhook' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Webhook URL
                </label>
                <input
                  type="url"
                  value={formData.config.url || ''}
                  onChange={(e) => updateConfig('url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                  placeholder="https://your-webhook-url.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  HTTP Method
                </label>
                <select
                  value={formData.config.method || 'POST'}
                  onChange={(e) => updateConfig('method', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>
              
              {/* Test Webhook Button - Only show for existing webhook integrations */}
              {integration && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Test Webhook</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Send a test webhook with sample data to verify your endpoint
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleTestWebhook}
                      disabled={testingWebhook || !formData.config.url}
                      className={`${buttonStyles.outline} flex items-center space-x-2 ${
                        testingWebhook || !formData.config.url ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Send className={`w-4 h-4 ${testingWebhook ? 'animate-pulse' : ''}`} />
                      <span>{testingWebhook ? 'Sending...' : 'Send Test'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {formData.integration_type === 'thank_you_page_customizer' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Page Title
                </label>
                <input
                  type="text"
                  value={formData.config.page_title || ''}
                  onChange={(e) => updateConfig('page_title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                  placeholder="Thank You!"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Background Color
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={formData.config.page_background_color || '#f9fafb'}
                    onChange={(e) => updateConfig('page_background_color', e.target.value)}
                    className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.config.page_background_color || '#f9fafb'}
                    onChange={(e) => updateConfig('page_background_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="#f9fafb"
                  />
                </div>
              </div>
              
              {/* Note about page builder */}
              <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Layout className="w-5 h-5 text-brand-600 dark:text-brand-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-brand-900 dark:text-brand-100">Page Builder</h4>
                    <p className="text-sm text-brand-700 dark:text-brand-300 mt-1">
                      After creating this integration, you can use the page builder to add sections like headers, paragraphs, images, videos, and call-to-action buttons.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(formData.integration_type === 'email_creator' || formData.integration_type === 'email_responder') && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Email Subject
                </label>
                <input
                  type="text"
                  value={formData.config.subject || ''}
                  onChange={(e) => updateConfig('subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                  placeholder="New form submission received"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Email Template (HTML)
                </label>
                <textarea
                  value={formData.config.template || ''}
                  onChange={(e) => updateConfig('template', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                  rows="6"
                  placeholder="<p>Thank you for your submission!</p>"
                  required
                />
              </div>
              {formData.integration_type === 'email_responder' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Theme Color
                  </label>
                  <input
                    type="color"
                    value={formData.config.color || '#3B82F6'}
                    onChange={(e) => updateConfig('color', e.target.value)}
                    className="w-full h-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
              )}
            </div>
          )}

          {/* Active Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-200">
              Enable this integration
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className={`${buttonStyles.outline} dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:border-gray-500`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${buttonStyles.primary}`}
            >
              {integration ? 'Update Integration' : 'Create Integration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default IntegrationManager;
